/* MARCA | 15 September 2026 | Quick ATC modal for the menswear lander, style-with, and shooting/country collection templates. */

if (!customElements.get('menswear-quick-atc-modal')) {
  class MenswearQuickAtcModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-menswear-quick-atc-body]');
      this.status = this.querySelector('[data-menswear-quick-atc-status]');
      this.closeButton = this.querySelector('[data-menswear-quick-atc-close]');
      this.cache = new Map();
      this.trigger = null;
      this.abortController = null;

      this.onDialogClick = this.onDialogClick.bind(this);
      this.onBodyClick = this.onBodyClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);

      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('close', this.onDialogClose);
      this.body.addEventListener('click', this.onBodyClick);
      this.body.addEventListener('change', this.onVariantChange);
      this.closeButton.addEventListener('click', () => this.close());
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.close();
    }

    onDialogClose() {
      this.setTriggerState(false);
      this.trigger = null;
    }

    onBodyClick(event) {
      const thumb = event.target.closest('[data-menswear-quick-atc-thumb]');
      if (thumb) {
        event.preventDefault();
        this.setActiveMedia(thumb.dataset.mediaId);
        return;
      }

      const colourway = event.target.closest('a.product__menswear-swatch');
      if (!colourway || !this.body.contains(colourway)) return;

      event.preventDefault();
      this.loadProduct(colourway.href, this.trigger);
    }

    onVariantChange(event) {
      if (!event.target.classList.contains('variant-option-radio-input')) return;
      this.syncVariantUI();
    }

    async openFrom(trigger) {
      this.trigger = trigger;
      this.setTriggerState(true, true);

      if (!this.dialog.open) {
        this.openDialogWithoutScroll();
      }

      await this.loadProduct(trigger.dataset.productUrl || trigger.href, trigger);
      this.setTriggerState(true, false);
    }

    async loadProduct(productUrl, trigger) {
      const fetchUrl = this.buildViewUrl(productUrl);
      if (!fetchUrl) return;

      this.dialog.setAttribute('aria-busy', 'true');
      this.status.hidden = false;
      this.body.replaceChildren();

      try {
        const html = await this.fetchProductHtml(fetchUrl);
        const productNode = this.extractProduct(html);
        if (!productNode) throw new Error('missing-product');

        this.status.hidden = true;
        this.withPreservedScroll(() => {
          this.body.replaceChildren(productNode);
          this.syncVariantUI();
          this.updateHeading();
          this.focusFirstField();
        });
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.renderError(trigger);
      } finally {
        this.dialog.setAttribute('aria-busy', 'false');
        this.status.hidden = true;
      }
    }

    buildViewUrl(productUrl) {
      try {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('view', 'menswear-quick-atc');
        return url.toString();
      } catch (error) {
        return '';
      }
    }

    async fetchProductHtml(fetchUrl) {
      if (this.cache.has(fetchUrl)) return this.cache.get(fetchUrl);

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const response = await fetch(fetchUrl, {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error('fetch-failed');

      const html = await response.text();
      this.cache.set(fetchUrl, html);
      return html;
    }

    extractProduct(html) {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      return wrapper.querySelector('.menswear-quick-atc__product');
    }

    renderError(trigger) {
      const error = document.createElement('div');
      error.className = 'menswear-quick-atc__error';

      const message = document.createElement('p');
      message.textContent = this.dialog.dataset.errorMessage || '';

      const link = document.createElement('a');
      link.className = 'menswear-quick-atc__view-product';
      link.href = trigger?.dataset?.productUrl || trigger?.getAttribute('href') || '#';
      link.textContent = this.dialog.dataset.viewProductLabel || '';

      error.append(message, link);
      this.body.replaceChildren(error);
    }

    syncVariantUI() {
      const productCard = this.body.querySelector('product-card');
      if (!productCard) return;

      const variantId = productCard.querySelector('input[name="id"]')?.value;
      const variant = productCard.variantsObj?.find(
        (item) => String(item.id) === String(variantId)
      );

      this.body.querySelectorAll('.js-product-card-swatches').forEach((group) => {
        const selected = group.querySelector('.variant-option-radio-input:checked');
        const label = group.querySelector('[data-selected-variant]');
        if (selected && label) label.textContent = selected.value;

        group.querySelectorAll('.button--variant').forEach((option) => {
          const input = option.querySelector('.variant-option-radio-input');
          option.classList.toggle('checked', Boolean(input?.checked));
        });
      });

      if (variant?.featured_media?.id) {
        this.setActiveMedia(variant.featured_media.id);
      }

      const submit = this.body.querySelector('.menswear-quick-atc__submit');
      const submitLabel = this.body.querySelector('[data-menswear-quick-atc-submit-label]');
      const submitPrice = this.body.querySelector('[data-menswear-quick-atc-submit-price]');
      const moneyFormat = this.body.querySelector('.price__container')?.dataset.moneyFormat;

      if (variant && submit) {
        const available = Boolean(variant.available);
        submit.toggleAttribute('disabled', !available);

        if (submitLabel) {
          submitLabel.textContent = available
            ? submit.dataset.addToCart || submitLabel.textContent
            : submit.dataset.soldOut || submitLabel.textContent;
        }

        if (submitPrice) {
          submitPrice.hidden = !available;
          if (available && typeof formatPrice === 'function' && moneyFormat) {
            submitPrice.textContent = formatPrice(moneyFormat, variant.price / 100);
          }
        }
      }
    }

    setActiveMedia(mediaId) {
      if (!mediaId) return;

      this.body.querySelectorAll('.menswear-quick-atc__slide').forEach((slide) => {
        const isActive = String(slide.dataset.mediaId) === String(mediaId);
        slide.classList.toggle('is-active', isActive);
        slide.toggleAttribute('hidden', !isActive);
      });

      this.body.querySelectorAll('[data-menswear-quick-atc-thumb]').forEach((thumb) => {
        const isActive = String(thumb.dataset.mediaId) === String(mediaId);
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    updateHeading() {
      const title = this.body.querySelector('.menswear-quick-atc__title');
      const heading = this.dialog.querySelector('h2.visually-hidden');
      if (title && heading) heading.textContent = title.textContent.trim();
    }

    /* MARCA | 14 September 2026 | Native showModal() and focusing off-screen radios both scroll the page to the dialog. Keep the lander where it is. */
    withPreservedScroll(callback) {
      const { scrollX, scrollY } = window;
      const root = document.documentElement;
      const previousBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';

      const restore = () => {
        window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' });
      };

      const result = typeof callback === 'function' ? callback() : undefined;
      restore();
      requestAnimationFrame(() => {
        restore();
        root.style.scrollBehavior = previousBehavior;
      });
      return result;
    }

    openDialogWithoutScroll() {
      this.withPreservedScroll(() => {
        this.dialog.showModal();
        this.dialog.focus({ preventScroll: true });
      });
    }

    focusFirstField() {
      this.dialog.focus({ preventScroll: true });
    }

    setTriggerState(expanded, loading = false) {
      document.querySelectorAll(`[aria-controls="${this.dialog.id}"]`).forEach((button) => {
        const isCurrent = button === this.trigger;
        button.setAttribute('aria-expanded', expanded && isCurrent ? 'true' : 'false');
        button.setAttribute('aria-busy', isCurrent && loading ? 'true' : 'false');
      });
    }

    close() {
      if (this.abortController) this.abortController.abort();
      if (this.dialog.open) this.dialog.close();
    }
  }

  customElements.define('menswear-quick-atc-modal', MenswearQuickAtcModal);
}

/* MARCA | 15 September 2026 | Document-level listeners so collection AJAX filters and load-more cards still open the modal. Capture pointerdown so the featured carousel cannot steal the click. */
function bindMenswearQuickAtcIcons() {
  if (document.documentElement.hasAttribute('data-menswear-quick-atc-delegated')) return;
  document.documentElement.setAttribute('data-menswear-quick-atc-delegated', '');

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.target.closest('[data-menswear-quick-atc-trigger]')) {
        event.stopPropagation();
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-menswear-quick-atc-trigger]');
    if (!trigger) return;

    const dialogId = trigger.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    const modal = dialog ? dialog.closest('menswear-quick-atc-modal') : null;
    if (!modal || typeof modal.openFrom !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    modal.openFrom(trigger);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindMenswearQuickAtcIcons);
} else {
  bindMenswearQuickAtcIcons();
}

